import { NumberFormatOptions } from '@internationalized/number';
import { Color, Spacing } from '@persona-id/design-tokens';
import { useNumberFormatter } from '@react-aria/i18n';
import { useSlider } from '@react-aria/slider';
import { useSliderState } from '@react-stately/slider';
import { SliderProps as SliderPropsType } from '@react-types/slider';
import { FocusEventHandler, ReactNode, useRef } from 'react';
import styled, { css } from 'styled-components';

import SliderThumb from 'design-system/components/Slider/SliderThumb';
import { paragraphMixins } from 'design-system/mixins/text';

const SliderContainer = styled('div')`
  display: flex;
  align-items: center;
  touch-action: none;
  position: relative;
`;

const SliderLabel = styled('div')`
  ${paragraphMixins.md};
  &:first-of-type {
    padding-right: ${Spacing[2]};
  }

  &:last-of-type {
    padding-left: ${Spacing[2]};
  }
`;

const SliderTrack = styled('div')<{
  track?: boolean;
  percents: number[];
  disabled?: boolean;
}>`
  position: relative;
  width: 100%;
  height: 4px;
  border-radius: 4px;
  outline: none;
  transition: background 0.2s;

  /* Disable clicks on slide bar */
  pointer-events: none;

  ${({ percents, track, disabled }) => {
    const activeColor = disabled ? Color.BorderInputDisabled : Color.CoreBlue700;
    const trackColor = Color.BorderDivider;

    if (track === false) {
      return css`
        background: ${Color.BorderDivider};
      `;
    }

    if (percents.length === 2) {
      return css`
        background: linear-gradient(
          to right,
          ${trackColor} 0%,
          ${trackColor} ${percents[0] * 100}%,
          ${activeColor} ${percents[0] * 100}%,
          ${activeColor} ${percents[1] * 100}%,
          ${trackColor} ${percents[1] * 100}%,
          ${trackColor} 100%
        );
      `;
    }

    const leftColor = activeColor;
    const rightColor = trackColor;

    return css`
      background: linear-gradient(
        to right,
        ${leftColor} 0%,
        ${leftColor} ${percents[0] * 100}%,
        ${rightColor} ${percents[0] * 100}%,
        ${rightColor} 100%
      );
    `;
  }}
`;

export interface SliderProps<T = number | number[]> {
  min?: number;
  minLabel?: ReactNode;
  max?: number;
  maxLabel?: ReactNode;
  onChange?: (value: T) => void;
  value?: T;
  defaultValue?: T;
  step?: number;

  /**
   * Show labels on ends of track
   */
  showLabels?: boolean;

  /**
   * Track style
   */
  track?: boolean;

  /**
   * Hide tooltips on slider thumbs
   */
  hideTooltips?: boolean;
  width?: number | 'auto';
  disabled?: boolean;
  formatOptions?: NumberFormatOptions;

  id?: string;
  className?: string;
  name?: string;
  onBlur?: FocusEventHandler<HTMLDivElement>;
  'aria-label'?: string;
  'aria-labelledby'?: string;
}

const Slider = ({
  minLabel,
  maxLabel,
  showLabels,
  track,
  width,
  hideTooltips,
  className,
  id,
  onBlur,
  ...elemProps
}: SliderProps) => {
  const { min, max, value, defaultValue, disabled } = elemProps;
  const trackRef = useRef(null);
  const numberFormatter = useNumberFormatter(elemProps.formatOptions);

  const ariaElemProps: SliderPropsType = {
    ...elemProps,
    isDisabled: disabled,
    minValue: min,
    maxValue: max,
    value: typeof value === 'number' ? [value] : value,
    defaultValue: typeof defaultValue === 'number' ? [defaultValue] : defaultValue,
    onChange:
      typeof elemProps.onChange !== 'undefined'
        ? (v) => {
            if (Array.isArray(v) && v.length === 1) {
              /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
              return elemProps.onChange(v[0]);
            }

            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            return elemProps.onChange(v);
          }
        : undefined,
  };

  const state = useSliderState({ ...ariaElemProps, numberFormatter });
  const { groupProps, trackProps, outputProps } = useSlider(ariaElemProps, state, trackRef);

  return (
    <SliderContainer
      {...groupProps}
      style={{
        position: 'relative',
        alignItems: 'center',
        width: width ?? 300,
        touchAction: 'none',
      }}
      className={className}
      id={id}
      onBlur={onBlur}
    >
      {showLabels && <SliderLabel>{minLabel ?? min}</SliderLabel>}
      <SliderTrack
        {...trackProps}
        track={track}
        ref={trackRef}
        percents={state.values.map((_v, i) => state.getThumbPercent(i))}
        disabled={disabled}
      >
        {state.values.map((_v, i) => (
          <SliderThumb
            key={`${trackProps.id}_thumb${i}`}
            index={i}
            state={state}
            trackRef={trackRef}
            hideTooltips={hideTooltips}
            outputProps={outputProps}
          />
        ))}
      </SliderTrack>
      {showLabels && <SliderLabel>{maxLabel ?? max}</SliderLabel>}
    </SliderContainer>
  );
};

export default Slider;
